/**
 * MyAgent API Service Factory
 * Platform-agnostic API wrappers for MyAgent RPC methods
 */

import { isAuthError } from 'workers-common'
import { resolveProviderAndKey } from './llm-config.js'

function resolveLlmConfig(botLlm, globalConfig) {
  const isLocal = botLlm?.mode === 'local' || globalConfig?.executionMode === 'local'
  if (!isLocal) return { mode: 'remote' }
  if (botLlm?.provider && (botLlm.apiKey || botLlm.provider === 'ollama')) {
    return { mode: 'local', provider: botLlm.provider, apiKey: botLlm.apiKey, model: botLlm.model }
  }
  return { mode: 'local', ...resolveProviderAndKey(globalConfig) }
}

/**
 * Create MyAgent API service
 * @param {Object} options
 * @param {string} options.workerName - Worker name for callByName
 * @param {Function} options.getClient - Function that returns client instance
 * @param {Function} [options.onAuthError] - Callback for authentication errors
 * @param {Function} [options.getSessionTarget] - Returns 'local' or worker name for session routing
 * @param {Object} [options.llmConfig] - LLM config service
 * @param {Function} [options.getFrontendLlmSdk] - Function to get frontend LLM SDK (mobile direct mode)
 * @param {string} [options.platform] - 'web' | 'mobile'
 * @returns {Object} API methods
 */
export function createApiService({ workerName, getClient, onAuthError, getSessionTarget, llmConfig, getFrontendLlmSdk, toolDefinitions, platform = 'web' }) {
  const callWithAuth = async (target, method, params) => {
    const client = getClient()
    try {
      return await client.callByNameWithAuth(target, method, params)
    } catch (error) {
      if (isAuthError(error) && onAuthError) {
        console.warn('[API] Authentication error detected:', error.message)
        onAuthError(error)
      }
      throw error
    }
  }

  const callWorker = (method, params) => callWithAuth(workerName, method, params)

  const callSession = async (method, params) => {
    const target = getSessionTarget ? await getSessionTarget() : workerName
    return callWithAuth(target, method, params)
  }

  const call = async (method, params) => {
    const client = getClient()
    return client.callByName(workerName, method, params)
  }

  return {
    // System APIs
    healthCheck: () => call('system.health', []),
    getAvailableTools: () => call('system.getAvailableTools', []),
    getAvailableModels: () => call('system.getAvailableModels', []),

    // Session APIs (routed to local or remote based on user config)
    getContext: (options = {}) => {
      const { maxMessages = 10000, conversationsOnly = true } = options
      return callSession('session.getContext', [{ maxMessages, conversationsOnly }])
    },
    clearSession: () => callSession('session.clear', [{}]),
    clearMainChat: () => callSession('session.clearMainChat', [{}]),
    getSessionStats: () => callSession('session.getStats', [{}]),
    getMessagesForLLM: () => callSession('session.getMessagesForLLM', [{}]),
    getChatMessages: (conversationId) => conversationId
      ? callSession('session.getConversationMessages', [{ conversationId, maxMessages: 50 }])
      : callSession('session.getContext', [{ maxMessages: 50, conversationsOnly: true }])
          .then(r => r.messages || []),
    saveStepToSession: (params) => callSession('session.saveStep', [params]),
    getParticipants: () => callSession('session.getParticipants', [{}]),
    setParticipants: (participants) => callSession('session.setParticipants', [{ participants }]),
    getConversationIndex: () => callSession('session.getConversationIndex', [{}]),
    createConversation: (params) => callSession('session.createConversation', [params]),
    deleteConversation: (conversationId) => callSession('session.deleteConversation', [{ conversationId }]),
    getConversationMessages: (conversationId, maxMessages) => callSession('session.getConversationMessages', [{ conversationId, maxMessages }]),
    saveConversationMessage: (conversationId, messages) => callSession('session.saveConversationMessage', [{ conversationId, messages }]),

    // Persist chat result — routes to conversation or main chat session based on conversationId
    saveChatResult: ({ conversationId, userMessage, responseMessage, toolInteractions, participantMessages }) => {
      if (conversationId) {
        const msgs = [{ role: 'user', content: userMessage, timestamp: Date.now() }]
        if (participantMessages?.length > 0) msgs.push(...participantMessages)
        if (responseMessage) msgs.push(responseMessage)
        return callSession('session.saveConversationMessage', [{ conversationId, messages: msgs }])
      }
      return callSession('session.saveStep', [{
        userMessage,
        toolInteractions,
        assistantMessage: (responseMessage && responseMessage.metadata?.type !== 'chat_pending') ? { content: responseMessage.content } : undefined,
        participantMessages,
      }])
    },

    // Unified chat - routes based on bot config's LLM mode, then falls back to global LLM config
    chat: async (params, botConfig = null) => {
      const globalConfig = llmConfig ? await llmConfig.getConfig() : null
      const { mode, provider, apiKey, model } = resolveLlmConfig(botConfig?.llm, globalConfig)

      if (mode === 'remote') return callWorker('chat.chatStep', [{ ...params, botConfig }])

      if (!provider || (!apiKey && provider !== 'ollama')) {
        throw new Error('No API key configured. Please configure your API key in settings.')
      }

      if (platform === 'mobile' && getFrontendLlmSdk) {
        return getFrontendLlmSdk().chatSingleStep({ provider, apiKey, model, messages: params.messages, tools: toolDefinitions })
      }

      return callWorker('llm.proxy', [{
        provider, apiKey, model, messages: params.messages, tools: toolDefinitions,
        toolCategories: botConfig?.toolCategories,
      }])
    },
  }
}
